import { BlogClient } from 'seobot';
import { BlogPost } from '@/types/blog';

// Helper function to convert text to Title Case
function toTitleCase(str: string): string {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

// Helper function to escape special regex characters
function escapeRegex(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Helper function to clean duplicate titles from HTML content
function cleanDuplicateTitle(htmlContent: string, title: string): string {
  if (!htmlContent || !title) {
    return htmlContent;
  }

  // Create patterns to match H1 tags with the title
  const h1Patterns = [
    // Match exact title in H1 tags (case insensitive)
    new RegExp(`^\\s*<h1[^>]*>\\s*${escapeRegex(title)}\\s*</h1>\\s*`, 'i'),
    // Match title with slight variations (punctuation, whitespace)
    new RegExp(`^\\s*<h1[^>]*>\\s*${escapeRegex(title.trim())}[\\s\\.,!?]*\\s*</h1>\\s*`, 'i'),
    // Match first 30 characters of title (handles truncated titles)
    new RegExp(`^\\s*<h1[^>]*>[^<]*${escapeRegex(title.substring(0, Math.min(30, title.length)))}[^<]*</h1>\\s*`, 'i')
  ];
  
  let cleanedContent = htmlContent.trim();
  
  // Try each pattern and remove the first match found
  for (const pattern of h1Patterns) {
    const newContent = cleanedContent.replace(pattern, '');
    if (newContent !== cleanedContent) {
      cleanedContent = newContent.trim();
      break; // Stop after first successful replacement
    }
  }
  
  return cleanedContent;
}

export class SEOBotService {
  private client: BlogClient | null;
  private cache: Map<string, any> = new Map();
  private cacheExpiry: number = 5 * 60 * 1000; // 5 minutes cache
  private isEnabled: boolean;
  
  constructor() {
    const apiKey = import.meta.env.VITE_SEOBOT_API_KEY;
    this.isEnabled = !!apiKey;
    
    if (!apiKey) {
      this.client = null;
    } else {
      this.client = new BlogClient(apiKey);
    }
  }
  
  // Method to clear cache and force refresh
  clearCache(): void {
    this.cache.clear();
  }

  // Force refresh by clearing cache and fetching new data
  async forceRefresh(): Promise<any[]> {
    this.clearCache();
    return await this.getArticles();
  }

  async getArticles(): Promise<any[]> {
    // Return empty array if SEOBot is disabled
    if (!this.isEnabled || !this.client) {
      return [];
    }
    
    const cacheKey = 'seobot_articles';
    const cached = this.cache.get(cacheKey);
    
    // Check if we have valid cached data
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    
    try {
      const response = await this.client.getArticles(0, 20); // Fetch up to 20 articles
      const articles = response.articles || [];
      
      // Fetch full article data for each article to get complete metadata including metaKeywords
      const fullArticles = await Promise.all(
        articles.map(async (article: any) => {
          try {
            const fullArticle = await this.client!.getArticle(article.slug);
            return fullArticle || article; // Fallback to basic article if full fetch fails
          } catch (error) {
            return article; // Return basic article data if full fetch fails
          }
        })
      );
      
      // Cache the results
      this.cache.set(cacheKey, {
        data: fullArticles,
        timestamp: Date.now()
      });
      
      return fullArticles;
    } catch (error) {
      // Return cached data if available, even if expired
      if (cached) {
        return cached.data;
      }
      
      return [];
    }
  }
  
  async getArticleBySlug(slug: string): Promise<any | null> {
    if (!this.isEnabled || !this.client) {
      return null;
    }
    
    try {
      // Directly fetch the full article by slug - this should include all content
      const fullArticle = await this.client.getArticle(slug);
      return fullArticle;
    } catch (error) {
      return null;
    }
  }
}

// Utility function to convert SEOBot article to BlogPost format
export function convertSEOBotToBlogPost(article: any): BlogPost {
  // Use createdAt if publishedAt is not available
  const publishDate = article.publishedAt || article.createdAt || new Date().toISOString();
  
  // Format date to YYYY-MM-DD
  const formattedDate = new Date(publishDate).toISOString().split('T')[0];
  
  return {
    slug: article.slug,
    title: article.headline,
    description: article.metaDescription || article.headline,
    date: formattedDate,
    author: 'CodeRide Team', // Default author for SEOBot articles
    image: article.image,
    imageAlt: `${article.headline} - CodeRide Blog`,
    excerpt: article.metaDescription || article.headline,
    content: (() => {
      if (article.html) {
        // Clean duplicate titles from HTML content
        return cleanDuplicateTitle(article.html, article.headline);
      } else if (article.markdown) {
        return article.markdown;
      } else {
        // Fallback content without duplicate title
        return `${article.metaDescription || 'Content coming soon...'}`;
      }
    })(),
    tags: (() => {
      // For SEOBot articles, prioritize metaKeywords as tags
      if (article.metaKeywords) {
        const keywordTags = article.metaKeywords
          .split(',')
          .map((k: string) => toTitleCase(k.trim()))
          .filter(Boolean)
          .slice(0, 6); // Limit to 6 tags for UI purposes
        return keywordTags;
      }
      
      // Fallback to structured tags if no meta keywords
      const structuredTags = article.tags?.map((tag: any) => toTitleCase(tag.title)) || [];
      
      if (structuredTags.length > 0) {
        return structuredTags;
      }
      
      return [];
    })(),
    featured: false, // SEOBot articles are not featured by default
    seo: {
      title: article.headline,
      description: article.metaDescription || article.headline,
      keywords: article.metaKeywords ? 
        article.metaKeywords.split(',').map((k: string) => k.trim()).filter(Boolean) : 
        []
    }
  };
}

// Create singleton instance
export const seobotService = new SEOBotService();
